import type { Editor } from '@tiptap/react'
import type { Context } from '@paintscout/util/templater'
import type { TemplateTag } from '@paintscout/util/util'
import { formatPlaceholderLabel } from './formatPlaceholderLabel'
import { ZERO_WIDTH_SPACE, buildPlaceholderHtml } from './placeholderUtils'

/** key -> author-supplied label, used to resolve nested `{{key}}` tokens to a display label. */
export function buildPlaceholderLabelMap(tags: TemplateTag[] = []): Record<string, string> {
  return tags.reduce(
    (acc, tag) => {
      acc[tag.key] = tag.label
      return acc
    },
    {} as Record<string, string>
  )
}

/**
 * Per-key: should *this* tag be inserted as an unresolved placeholder chip rather than a real
 * value? True when there is no context at all (e.g. the presentation editor), or when the
 * context's value for this key is itself a `{{...}}` token (the identity context from
 * `getTemplateContext()`).
 *
 * NOTE: deliberately *not* the same predicate as `isTemplateEditingContext` below — see its
 * comment. Both are faithful to the behaviour that shipped; merging them is a follow-up.
 */
export function isTemplateMode(key: string, templateContext?: Context): boolean {
  if (!templateContext) return true
  const contextValue = templateContext[key]
  return typeof contextValue === 'string' && /\{\{[^}]+\}\}/.test(contextValue)
}

/**
 * Whole-context: is the editor authoring a template at all? Used by `Editor` to decide whether
 * content is round-tripped through `toTemplate()`/`toPlaceholder()` on load and save.
 *
 * This samples only the first key and demands *exact* equality with `{{key}}`, where
 * `isTemplateMode` checks a single key and merely looks for an embedded token. For the two
 * contexts that actually exist — the identity context (every value is `{{key}}`) and a
 * real-value context (no value is) — the two agree. They can only disagree on a mixed context,
 * which nothing produces today. Unifying them means widening this one, and this one gates the
 * save path, so it isn't a safe drive-by: a real-value context whose first value happened to
 * contain `{{` would start rewriting stored content.
 */
export function isTemplateEditingContext(templateContext?: Context): boolean {
  if (!templateContext) return true
  const sampleKey = Object.keys(templateContext)[0]
  return sampleKey ? (templateContext[sampleKey] as string) === `{{${sampleKey}}}` : true
}

/**
 * Renders a `Context` value as the plain text that stands in for a tag — both the preview under a
 * menu item and the text actually inserted in real-value mode. `Context` values aren't all strings:
 * numeric ones (quote number, totals) are legitimate and get stringified, while objects/arrays/
 * booleans have no sensible one-line rendering and `NaN`/`Infinity` are arithmetic accidents, so
 * those are dropped. An empty string is treated as absent too, so callers fall back to `{{key}}`.
 */
export function stringifyContextValue(value?: unknown): string | undefined {
  if (typeof value === 'string') return value || undefined
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return undefined
}

/** Turns a context value that still contains `{{key}}` tokens into placeholder-chip HTML. */
function expandTemplatePlaceholders(input: string, labelMap: Record<string, string>): string {
  const html = input.replace(/\n/g, '<br/>')
  return html.replace(/\{\{(\w+)\}\}/g, (_, key) =>
    buildPlaceholderHtml(key, formatPlaceholderLabel(key, labelMap[key]))
  )
}

export type TemplateTagInsert =
  /** A pre-composed, multi-tag value (estimatorInfo) — insert its expanded HTML as-is. */
  | { mode: 'expand'; html: string }
  /** Authoring a template — insert an atomic placeholder chip labelled `displayValue`. */
  | { mode: 'placeholder'; displayValue: string }
  /** A real value is known — insert it as plain text. */
  | { mode: 'literal'; value: string }

export function resolveTemplateTagInsert(args: {
  tag: TemplateTag
  tags?: TemplateTag[]
  templateContext?: Context
  /**
   * Always insert a placeholder chip, regardless of what `templateContext` resolves this key to.
   * The presentation editor passes a real-value context (so the menu can preview each tag's
   * current value), but presentations are templates reused across quotes — a tag must stay a chip
   * even when the context it was opened against happens to have a real value for that key.
   */
  forcePlaceholder?: boolean
}): TemplateTagInsert {
  const { tag, tags, templateContext, forcePlaceholder } = args
  // Not cast to string: a numeric tag (quote number, totals) reaches the literal path below, and
  // handing a number straight to `insertContent` is not something tiptap accepts.
  const contextValue = stringifyContextValue(templateContext?.[tag.key])
  const templateMode = forcePlaceholder || isTemplateMode(tag.key, templateContext)

  if (templateMode) {
    // TODO: `estimatorInfo` is the one tag whose value is a composite of other tags, so it expands
    // rather than becoming a single chip. Special-casing the key here works but doesn't scale —
    // if a second composite tag ever appears, put a `composite?: boolean` on `TemplateTag` instead.
    if (tag.key === 'estimatorInfo' && contextValue) {
      return { mode: 'expand', html: expandTemplatePlaceholders(contextValue, buildPlaceholderLabelMap(tags)) }
    }
    return { mode: 'placeholder', displayValue: formatPlaceholderLabel(tag.key, tag.label) }
  }

  // `??`, not `||`: a total of 0 stringifies to "0", which is a real value and must not fall
  // through to an unresolved `{{total}}`.
  return { mode: 'literal', value: contextValue ?? `{{${tag.key}}}` }
}

/**
 * Inserts a template tag at the editor's current selection. Shared by every surface that offers
 * a tag picker (the full toolbar, the bubble menu and the floating menu) so insertion behaviour
 * can never drift between them.
 *
 * `replaceSelection` decides what a non-empty selection means. In the always-visible toolbar,
 * selecting text and picking a tag reads as "swap this text for the tag", so it defaults to true
 * (`insertContent` replaces the selection). The bubble menu is different: it only appears *because*
 * text is selected, so the selection carries no intent — there, pass false and the tag is inserted
 * after the selection instead of eating it.
 */
export function insertTemplatePlaceholder(args: {
  editor: Editor | null
  tag: TemplateTag
  tags?: TemplateTag[]
  templateContext?: Context
  forcePlaceholder?: boolean
  replaceSelection?: boolean
}): void {
  const { editor, tag, tags, templateContext, forcePlaceholder, replaceSelection = true } = args
  if (!editor) return

  const insert = resolveTemplateTagInsert({ tag, tags, templateContext, forcePlaceholder })

  const { from: selectionFrom, to: selectionTo } = editor.state.selection
  // Collapse to the end of the selection first when we're preserving it. `null` = leave the
  // selection alone, so the toolbar's chain stays byte-for-byte what it was.
  const collapseTo = replaceSelection || selectionFrom === selectionTo ? null : selectionTo
  const startChain = () => {
    const chain = editor.chain().focus()
    return collapseTo === null ? chain : chain.setTextSelection(collapseTo)
  }

  if (insert.mode === 'expand') {
    startChain().insertContent(insert.html).run()
    return
  }

  if (insert.mode === 'literal') {
    startChain().insertContent(insert.value).run()
    return
  }

  const from = collapseTo ?? selectionFrom
  const to = from + insert.displayValue.length
  // Land the caret just past the trailing ZWS (one character) we insert below.
  const cursorPosition = to + ZERO_WIDTH_SPACE.length
  startChain()
    .insertContent(insert.displayValue)
    .setTextSelection({ from, to })
    .setMark('placeholder', { key: tag.key, label: insert.displayValue })
    .setTextSelection(to)
    .unsetMark('placeholder')
    .insertContent(ZERO_WIDTH_SPACE)
    .setTextSelection(cursorPosition)
    .run()
}
